import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Eye,
  Funnel,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useFacilityReports } from "./hooks";
import ConfigurableTableLayout from "../../Common/ConfigurableTableLayout";
import ColumnTextFilterPopover from "../../FacilityListComponents/RefactoredFacilityTable/ColumnTextFilterPopover";
import ModalConfirm from "../../ModalConfirm";

import "./styles.scss";

type ReportColumnId =
  | "facility_name"
  | "created_at"
  | "start_date"
  | "end_date"
  | "status"
  | "actions";

type SortField =
  | "location.name"
  | "created_at"
  | "start_date"
  | "end_date"
  | "status";
type SortOrder = "asc" | "desc";

const ALL_COLUMNS: ReportColumnId[] = [
  "facility_name",
  "created_at",
  "start_date",
  "end_date",
  "status",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: ReportColumnId[] = [...ALL_COLUMNS];
const VISIBLE_COLUMNS_STORAGE_KEY =
  "FacilityReportsPage_RefactoredVisibleColumns";

const COLUMN_SORT_FIELD: Partial<Record<ReportColumnId, SortField>> = {
  facility_name: "location.name",
  created_at: "created_at",
  start_date: "start_date",
  end_date: "end_date",
  status: "status",
};

const normalizeVisibleColumns = (value: unknown): ReportColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const filtered = value.filter((col): col is ReportColumnId =>
    ALL_COLUMNS.includes(col as ReportColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<ReportColumnId>(["facility_name", "actions"]);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

export const FacilityReportsList: React.FC = () => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string, args?: any) =>
      _t(`facilityReportsPage.facilityReportsList.${key}`, args),
    [_t]
  );

  const {
    query,
    filterFormData,
    handleChangeFilterFormData,
    searchParams,
    setSearchParams,
    totalCount,
    deleteReport,
  } = useFacilityReports();

  const facilityReports = query.data?.data;

  const [visibleColumns, setVisibleColumns] = useState<ReportColumnId[]>(() => {
    const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!savedColumns) {
      return DEFAULT_VISIBLE_COLUMNS;
    }

    try {
      return normalizeVisibleColumns(JSON.parse(savedColumns));
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);

  const history = useHistory();

  const handleViewReport = (facilityReportId: number) => {
    history.push(`/facility-reports/${facilityReportId}`);
  };

  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<
    number | undefined
  >(undefined);
  const confirmDeleteFacilityReport = facilityReports?.find(
    (fr) => fr.id === confirmDeleteModalOpen
  );

  const handleDeleteReport = (facilityReportId: number) => {
    setConfirmDeleteModalOpen(facilityReportId);
  };

  const handleConfirmDeleteReport = async () => {
    if (!confirmDeleteModalOpen) return;
    deleteReport(confirmDeleteModalOpen);
    setConfirmDeleteModalOpen(undefined);
  };

  const currentSortField = (searchParams.sort as SortField) || "location.name";
  const currentSortOrder: SortOrder =
    searchParams.order === "desc" ? "desc" : "asc";

  const activeConditionChips = useMemo(() => {
    const sortLabelByField: Record<SortField, string> = {
      "location.name": t("table.header.facility_name.default"),
      created_at: t("table.header.created_at.default"),
      start_date: t("table.header.start_date.default"),
      end_date: t("table.header.end_date.default"),
      status: t("table.header.status.default"),
    };

    const chips: { id: string; label: string; onRemove: () => void }[] = [
      {
        id: `sort-${currentSortField}`,
        label: `並び替え順: ${sortLabelByField[currentSortField]} ${
          currentSortOrder === "desc" ? "降順" : "昇順"
        }`,
        onRemove: () => {
          setSearchParams((sp) => ({
            ...sp,
            sort: "location.name",
            order: "asc",
            offset: 0,
          }));
        },
      },
    ];

    const locationName = (searchParams.location_name || "").trim();
    if (locationName.length > 0) {
      chips.push({
        id: "location-name-filter",
        label: `${t("table.header.facility_name.default")}: ${locationName}`,
        onRemove: () => {
          setSearchParams((sp) => ({
            ...sp,
            location_name: undefined,
            offset: 0,
          }));
        },
      });
    }

    return chips;
  }, [
    currentSortField,
    currentSortOrder,
    searchParams.location_name,
    setSearchParams,
    t,
  ]);

  const onSelectSortField = (columnId: ReportColumnId) => {
    const field = COLUMN_SORT_FIELD[columnId];
    if (!field) {
      return;
    }

    setSearchParams((sp) => {
      const nextOrder: SortOrder =
        sp.sort === field ? (sp.order === "asc" ? "desc" : "asc") : "asc";

      return {
        ...sp,
        sort: field,
        order: nextOrder,
        offset: 0,
      };
    });
  };

  const createSortButton = (columnId: ReportColumnId, labelText: string) => {
    const sortField = COLUMN_SORT_FIELD[columnId];
    if (!sortField) {
      return <span className="column-label-text">{labelText}</span>;
    }

    const isActive = currentSortField === sortField;
    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(columnId)}
      >
        <span>{labelText}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            currentSortOrder === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const renderFilterTrigger = (columnId: ReportColumnId) => {
    if (columnId !== "facility_name") {
      return null;
    }

    const hasActiveFilter =
      (searchParams.location_name || "").trim().length > 0;

    return (
      <div className="filter-trigger-wrap">
        <button
          type="button"
          className={`filter-trigger-btn ${
            hasActiveFilter ? "has-filter" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            setActiveFilterColumn((prev) =>
              prev === columnId ? null : columnId
            );
          }}
          aria-label={`filter-${columnId}`}
        >
          <Funnel className="funnel-icon" size={14} strokeWidth={2} />
        </button>
        {activeFilterColumn === columnId && (
          <ColumnTextFilterPopover
            columnId={columnId}
            title={t("table.header.facility_name.default")}
            value={searchParams.location_name || ""}
            onApply={(value) => {
              setSearchParams((sp) => ({
                ...sp,
                location_name: value || undefined,
                offset: 0,
              }));
            }}
            onClear={() => {
              setSearchParams((sp) => ({
                ...sp,
                location_name: undefined,
                offset: 0,
              }));
            }}
            onClose={() => setActiveFilterColumn(null)}
          />
        )}
      </div>
    );
  };

  const toggleColumnVisibility = (columnId: ReportColumnId) => {
    if (columnId === "facility_name" || columnId === "actions") {
      return;
    }

    setVisibleColumns((prev) => {
      const next = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getColumnLabelText = (columnId: ReportColumnId) => {
    switch (columnId) {
      case "facility_name":
        return t("table.header.facility_name.default");
      case "created_at":
        return t("table.header.created_at.default");
      case "start_date":
        return t("table.header.start_date.default");
      case "end_date":
        return t("table.header.end_date.default");
      case "status":
        return t("table.header.status.default");
      case "actions":
        return "操作";
    }
  };

  return (
    <>
      <div className="line"></div>
      <div className="white-border-panel facility-reports-list">
        <h2 className="facility-reports-list-title">{t("reports")}</h2>
        <ConfigurableTableLayout
          className="facility-reports-table-layout"
          onContainerClick={() => {
            setShowColumnSettings(false);
            setOpenActionMenuId(null);
          }}
          controls={
            <div
              className="column-settings-container"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="btn-column-settings"
                onClick={() => setShowColumnSettings((prev) => !prev)}
              >
                表示項目の設定 ⚙️
              </button>
              {showColumnSettings && (
                <div className="column-settings-dropdown">
                  <div className="dropdown-title">表示列の切り替え</div>
                  {ALL_COLUMNS.map((colId) => (
                    <label
                      key={`setting-${colId}`}
                      className={`setting-label ${
                        colId === "actions" || colId === "facility_name"
                          ? "is-fixed-disabled"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(colId)}
                        disabled={
                          colId === "facility_name" || colId === "actions"
                        }
                        onChange={() => toggleColumnVisibility(colId)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="label-text">
                        {getColumnLabelText(colId)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          }
          conditionChips={activeConditionChips}
          onClearAllConditions={() => {
            setSearchParams((sp) => ({
              ...sp,
              sort: "location.name",
              order: "asc",
              location_name: undefined,
              offset: 0,
            }));
          }}
          clearAllLabel={t("controls.clear")}
          tableContent={
            <div className="facility-reports-table-scroll">
              <table className="facility-reports-list-content">
                <thead>
                  <tr className="refactored-facility-list-header">
                    {visibleColumns.includes("facility_name") && (
                      <th className="facility-name">
                        <div className="header-cell-inner">
                          {createSortButton(
                            "facility_name",
                            getColumnLabelText("facility_name")
                          )}
                          {renderFilterTrigger("facility_name")}
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes("created_at") && (
                      <th className="created-at">
                        <div className="header-cell-inner">
                          {createSortButton(
                            "created_at",
                            getColumnLabelText("created_at")
                          )}
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes("start_date") && (
                      <th className="start-date">
                        <div className="header-cell-inner">
                          {createSortButton(
                            "start_date",
                            getColumnLabelText("start_date")
                          )}
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes("end_date") && (
                      <th className="end-date">
                        <div className="header-cell-inner">
                          {createSortButton(
                            "end_date",
                            getColumnLabelText("end_date")
                          )}
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes("status") && (
                      <th className="status">
                        <div className="header-cell-inner">
                          {createSortButton(
                            "status",
                            getColumnLabelText("status")
                          )}
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes("actions") && (
                      <th className="actions"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {facilityReports &&
                    facilityReports.map((facilityReport) => (
                      <tr key={facilityReport.id}>
                        {visibleColumns.includes("facility_name") && (
                          <td
                            className="facility-name"
                            title={facilityReport.location.name}
                          >
                            <span className="cell-ellipsis">
                              {facilityReport.location.name}
                            </span>
                          </td>
                        )}
                        {visibleColumns.includes("created_at") && (
                          <td className="created-at">
                            {dayjs(facilityReport.created_at).format(
                              "YYYY/MM/DD"
                            )}
                          </td>
                        )}
                        {visibleColumns.includes("start_date") && (
                          <td className="start-date">
                            {dayjs(facilityReport.start_date).format(
                              "YYYY/MM/DD"
                            )}
                          </td>
                        )}
                        {visibleColumns.includes("end_date") && (
                          <td className="end-date">
                            {dayjs(facilityReport.end_date).format(
                              "YYYY/MM/DD"
                            )}
                          </td>
                        )}
                        {visibleColumns.includes("status") && (
                          <td className={`status ${facilityReport.status}`}>
                            {t(`status.${facilityReport.status}`)}
                          </td>
                        )}
                        {visibleColumns.includes("actions") && (
                          <td className="actions">
                            <div
                              className="action-menu"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="action-menu-trigger"
                                aria-label="操作メニューを開く"
                                aria-expanded={
                                  openActionMenuId === facilityReport.id
                                }
                                onClick={() => {
                                  setOpenActionMenuId((currentId) =>
                                    currentId === facilityReport.id
                                      ? null
                                      : facilityReport.id
                                  );
                                }}
                              >
                                <MoreHorizontal size={20} aria-hidden="true" />
                              </button>
                              {openActionMenuId === facilityReport.id && (
                                <div className="action-menu-dropdown">
                                  <button
                                    type="button"
                                    className="action-menu-item"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      handleViewReport(facilityReport.id);
                                    }}
                                  >
                                    <Eye size={16} aria-hidden="true" />
                                    <span>{t("view")}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="action-menu-item is-danger"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      handleDeleteReport(facilityReport.id);
                                    }}
                                  >
                                    <Trash2 size={16} aria-hidden="true" />
                                    <span>{t("delete")}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          }
          pagination={{
            filterFormData,
            totalRowsNumber: totalCount,
            onChangeFilterFormData: handleChangeFilterFormData,
          }}
        />
      </div>
      {confirmDeleteModalOpen && (
        <ModalConfirm
          title={t("confirm_delete_report.title")}
          description={t("confirm_delete_report.description", {
            locationName: confirmDeleteFacilityReport?.location.name,
            startDate: dayjs(confirmDeleteFacilityReport?.start_date).format(
              "YYYY/MM/DD"
            ),
            endDate: dayjs(confirmDeleteFacilityReport?.end_date).format(
              "YYYY/MM/DD"
            ),
          })}
          cancelLabel={t("confirm_delete_report.cancel")}
          confirmLabel={t("confirm_delete_report.delete_confirm")}
          onClose={() => setConfirmDeleteModalOpen(undefined)}
          onConfirm={handleConfirmDeleteReport}
          color="red"
          translate={false}
        />
      )}
    </>
  );
};
